<template>
  <div>
    <div v-show="expanded" class="form-bordered">
      <div class="alert alert-danger" v-if="!active">
        <strong>This membership option is not active—new members cannot sign up for it.</strong>
        Existing members will keep paying the price they signed up for initially. If you need to do a price raise
        for your existing members please contact support@wrestlingiq.com for assistance processing the price raise.
      </div>
      <div class="form-group">
        <label for="price_name">Name</label>
        <input id="price_name" v-model="name" type="text" class="form-control">
      </div>
      <div class="form-group">
        <div class="row">
          <div class="col-sm-4">
            <div>
              <label>Recurring Amount</label>
              <money v-model="amount" v-bind="money" class="form-control" :disabled="existingId"></money>
            </div>
            <div class="m-t-sm">
              <label>One-time Registration Fee</label>
              <money v-model="oneTimeFeeAmount" v-bind="money" class="form-control" :disabled="currentBillingPartner !== 'justifi'"></money>
              <p v-if="currentBillingPartner === 'justifi'">
                <small>If you charge a one-time fee for enrollment/initiation, this amount will be added to the first charge.</small>
              </p>
              <div v-else class="alert alert-warning m-t-sm">
                <p>
                  <small>
                    One-time fees are only available to teams using WrestlingIQ Payments for billing. Please email support@wrestlingiq.com if you want to switch over to WrestlingIQ Payments.
                  </small>
                </p>
              </div>
            </div>
          </div>
          <div class="col-sm-8">
            <label>Every</label>
            <div class="row">
              <div class="col-sm-3">
                <input ref="intervalCountInput" v-model="intervalCount" type="number" class="form-control" :disabled="existingId"/>
              </div>
              <div class="col-sm-9">
                <select v-model="interval" class="form-control" :disabled="existingId">
                  <option selected disabled>Choose interval...</option>
                  <option value="week">Week(s)</option>
                  <option value="month">Month(s)</option>
                  <option value="year">Year(s)</option>
                </select>
              </div>
            </div>
            <div class="m-t-sm">
              <label>Until</label>
              <div class="row">
                <div class="col-sm-6">
                  <select v-model="membershipLength" class="form-control" :disabled="existingId">
                    <option value="indefinite">Cancelled</option>
                    <option value="limited">A set number of billing cycles...</option>
                  </select>
                </div>
                <div class="col-sm-6" v-if="!indefinite">
                  <div class="input-group">
                    <input ref="intervalsToBillInput" v-model="intervalsToBill" class="form-control" type="number" :disabled="existingId">
                    <span class="input-group-addon">bill(s)</span>
                  </div>
                </div>
                <div class="col-sm-12 m-t-xs" v-if="!indefinite">
                  Memberships will expire {{ intervalCount * intervalsToBill }} {{ interval }}<span v-if="intervalCount * intervalsToBill > 1">(s)</span> after signing up.
                </div>
              </div>
            </div>
          </div>
          <div class="col-sm-12" v-if="existingId">
            <div class="alert alert-warning m-t-sm">
              <p class="">
                <small>
                  Most price details are not editable, because it could impact existing members.
                  If you need to change prices, we recommend creating a brand new price or membership option.
                  If no one is subscribed to this price, please contact support@wrestlingiq.com to get it removed.
                </small>
              </p>
            </div>
          </div>
        </div>
      </div>
      <div class="form-group">
        <div class="row">
          <div :class="limitClassesPerWeek ? 'col-sm-4' : 'col-sm-12'">
            <div class="flex align-items-start">
              <input type="checkbox" :id="`limit_classes_per_week-${fieldUid}`" name="limit_classes_per_week" v-model="limitClassesPerWeek" :aria-checked="limitClassesPerWeek" @change="onLimitClassesPerWeekChange">
              <div class="m-l-sm">
                <label :for="`limit_classes_per_week-${fieldUid}`" class="m-b-none">
                  Limit classes per week?
                </label>
                <p>
                  Leave off for unlimited (the default). For "1x/week", "2x/week", etc. plans, set a limit and the kiosk will block check-in once the wrestler has used their weekly classes; an admin PIN can override.
                </p>
              </div>
            </div>
          </div>
          <div class="col-sm-8" v-if="limitClassesPerWeek">
            <label :for="`class_pass_quantity-${fieldUid}`">Classes included per week</label>
            <div class="input-group" style="max-width: 220px;">
              <input
                :id="`class_pass_quantity-${fieldUid}`"
                ref="classPassInput"
                v-model.number="classPassQuantity"
                type="number"
                min="1"
                step="1"
                class="form-control"
              >
              <span class="input-group-addon">/ week</span>
            </div>
          </div>
        </div>
      </div>
      <div class="form-group">
        <div class="row">
          <div class="col-sm-4">
            <div class="flex align-items-start">
              <input type="checkbox" name="sibdiscount" v-model="siblingDiscountEnabled" :aria-checked="siblingDiscountEnabled">
              <div class="m-l-sm">
                <label for="sibdiscount" class="m-b-none">
                  Do you offer a sibling discount?
                </label>
                <p class="m-t-xs" v-if="siblingDiscountEnabled && legacyCouponBehavior">
                  This pricing tier is set to auto-apply coupons to all wrestlers (instead of just the 2nd or 3rd wrestler).
                  If you want that changed, please email support@wrestlingiq.com and we will handle it.
                </p>
              </div>
            </div>
          </div>
          <div class="col-sm-8" v-if="siblingDiscountEnabled">
            <div>
              <label>Scholarship to automatically apply to 2nd wrestler signing up:</label>
              <select v-model="siblingCoupon" class="form-control" name="sibling_coupon">
                <option value="-1"></option>
                <option v-for="c in availableCoupons" :value="c.id">
                  {{ c.code }}
                  <template v-if="c.amount_off > 0">
                    ({{ formatAsCurrency(c.amount_off) }})
                  </template>
                  <template v-else>
                    ({{ c.percent_off * 100 }}% off)
                  </template>
                </option>
              </select>
            </div>
            <div class="m-t-sm">
              <label>Scholarship to automatically apply to 3rd+ sibling signing up:</label>
              <select v-model="thirdSiblingCoupon" class="form-control" name="third_sibling_coupon">
                <option value="-1"></option>
                <option v-for="c in availableCoupons" :value="c.id">
                  {{ c.code }}
                  <template v-if="c.amount_off > 0">
                    ({{ formatAsCurrency(c.amount_off) }})
                  </template>
                  <template v-else>
                    ({{ c.percent_off * 100 }}% off)
                  </template>
                </option>
              </select>
            </div>
            <p class="text=sm text-gray-600 m-t-xs">
              In most cases, these be the same, unless you do tiered discounts (e.g. 2nd wrestler gets $50 off, 3rd+ wrestler in a family gets $100 off.)
            </p>
          </div>
        </div>
      </div>
      <div class="form-group" v-if="eligibleForAnchoring">
        <div class="row">
          <div :class="anchorDayOfMonthEnabled ? 'col-sm-4' : 'col-sm-12'">
            <div class="flex align-items-start">
              <input type="checkbox" name="anchor_day_of_month" v-model="anchorDayOfMonthEnabled" :aria-checked="anchorDayOfMonthEnabled" :disabled="currentBillingPartner !== 'justifi' || !indefinite || deferBillingEnabled">
              <div class="m-l-sm">
                <label for="anchor_day_of_month" class="m-b-none">
                  Anchor billing to the 1st or 15th of the month?
                </label>
                <p>
                  With this option, subscriptions will be billed on the 1st or 15th of the month. The first invoice will be prorated based on the day of the month when the subscription was created.
                </p>
                <div v-if="currentBillingPartner !== 'justifi'" class="alert alert-warning m-t-sm">
                  <p>
                    <small>
                      Anchor dates are only available to teams using WrestlingIQ Payments. Please email support@wrestlingiq.com if you want to switch over to WrestlingIQ Payments.
                    </small>
                  </p>
                </div>
                <div v-else-if="!indefinite" class="alert alert-warning m-t-sm">
                  <p>
                    <small>
                      Anchor dates are only available for indefinite memberships.
                    </small>
                  </p>
                </div>
                <div v-else-if="deferBillingEnabled" class="alert alert-warning m-t-sm">
                  <p>
                    <small>
                      Anchor billing cannot be combined with deferred billing. Disable deferred billing first if you want to use anchor billing.
                    </small>
                  </p>
                </div>
              </div>
            </div>
          </div>
          <div class="col-sm-8" v-if="anchorDayOfMonthEnabled">
            <div>
              <label>Anchor billing date to</label>
              <select v-model="anchorDayOfMonth" class="form-control" name="anchor_day_of_month">
                <option value="1">1st of the month</option>
                <option value="15">15th of the month</option>
              </select>
            </div>
            <div v-if="existingId">
              <div class="alert alert-danger m-t-sm">
                <strong>NOTE:</strong> Changing the anchor day will <strong>only impact new subscribers</strong>. Existing subscriptions will keep their current billing schedule.
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="form-group" v-if="currentBillingPartner === 'justifi'">
        <div class="row">
          <div :class="deferBillingEnabled ? 'col-sm-4' : 'col-sm-12'">
            <div class="flex align-items-start">
              <input type="checkbox" name="defer_billing" v-model="deferBillingEnabled" :aria-checked="deferBillingEnabled" :disabled="anchorDayOfMonthEnabled">
              <div class="m-l-sm">
                <label for="defer_billing" class="m-b-none">
                  Defer billing to a future date?
                </label>
                <p>
                  With this option, members can sign up now but won't be charged until the date you select. This is useful for pre-sales.
                </p>
                <div v-if="anchorDayOfMonthEnabled" class="alert alert-warning m-t-sm">
                  <p>
                    <small>
                      Deferred billing cannot be combined with anchor billing. Disable anchor billing first if you want to use deferred billing.
                    </small>
                  </p>
                </div>
              </div>
            </div>
          </div>
          <div class="col-sm-8" v-if="deferBillingEnabled">
            <div class="form-group flex flex-col gap-xs">
              <label>Billing start date</label>
              <date-picker :date="billingStartsAt" @input="billingStartsAt = $event" :disabled-date="disablePastDates"></date-picker>
            </div>
          </div>
        </div>
      </div>
      <div class="row">
          <div class="col-sm-12">
            <div class="form-group flex align-items-start">
              <input type="checkbox" name="active" v-model="active" :aria-checked="active">
              <div class="m-l-sm">
                <label for="active" class="m-b-none">
                  Is this membership option active?
                </label>
                <p class="text-gray-600">
                  If not active, new members cannot sign up for this price. Existing members will remain on this price, even if it is inactive.
                </p>
              </div>
            </div>
          </div>
        </div>
      <div class="button-area">
        <div v-show="error">
          <small>
            <span class="text-danger">{{ errorMessage }}</span>
          </small>
        </div>
        <button class="btn btn-default" @click.prevent="cancel">Cancel</button>
        <button class="btn btn-primary" @click.prevent="validate">
          Done
        </button>
      </div>
    </div>
    <div v-show="!expanded">
      <div class="card-bordered" :class="{ 'opacity-50 bg-gray-50': !active }">
        <div class="row">
          <div class="col-xs-12 col-sm-6">
            {{ name }}
            <span class="text-success" v-if="draft"> - Draft</span>
            <span class="text-danger" v-if="!active"> - Inactive</span>
            <br/>
            {{ formatAsCurrency(this.amount * 100) }} {{ prettyInterval }}
            <span v-if="oneTimeFeeAmount > 0"><br>+ {{ formatAsCurrency(oneTimeFeeAmount * 100) }} one-time registration fee</span>
            <span v-if="!indefinite">
              <br>
              Memberships expire after {{ intervalCount * intervalsToBill }} {{ interval }}<span v-if="intervalCount * intervalsToBill > 1">s</span>
            </span>
            <span v-if="anchorDayOfMonthEnabled && anchorDayOfMonth && eligibleForAnchoring">
              <br>
              Billing anchored to the {{ anchorDayOfMonth === '1' || anchorDayOfMonth === 1 ? '1st' : '15th' }} of the month
            </span>
            <span v-if="deferBillingEnabled && billingStartsAt">
              <br>
              Billing starts on {{ billingStartsAt.format('M/D/YYYY') }}
            </span>
            <span v-if="siblingDiscountEnabled && selectedCouponId">
              <br>
              Sibling discount: {{ selectedCouponCode }}
            </span>
            <span v-if="siblingDiscountEnabled && selectedThirdCouponId && selectedThirdCouponId !== selectedCouponId">
              <br>
              Sibling discount: {{ selectedThirdCouponCode }} for 3rd+ wrestler
            </span>
            <span v-if="classPassQuantity > 0">
              <br>
              {{ classPassQuantity }} class<span v-if="classPassQuantity > 1">es</span> per week
            </span>
          </div>
          <div class="col-xs-12 col-sm-6 actions">
            <a @click.prevent="toggleExpanded">Edit</a>
            <a v-if="!existingId" class="text-danger" @click.prevent="removeDraft">Discard</a>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import { Money } from 'v-money';
import DatePicker from './date_picker.vue';
import { formattingMixin } from './mixins/formatting_mix';
import { errorableMix } from './mixins/errorable_mix';

export default {
  name: 'subscription-price-form',
  mixins: [formattingMixin, errorableMix],
  components: {
    Money,
    DatePicker,
  },
  props: {
    price: {
      type: Object,
      required: true,
    },
    currentBillingPartner: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      draft: false,
      existingId: null,
      name: '',
      amount: 0,
      oneTimeFeeAmount: 0,
      active: true,
      interval: null,
      currency: 'usd',
      intervalCount: 1,
      isNewOption: false,
      membershipLength: 'indefinite',
      intervalsToBill: 1,
      siblingDiscountEnabled: false,
      couponStrategy: 'tiers',
      siblingCoupon: -1,
      thirdSiblingCoupon: -1,
      availableCoupons: [],
      // Whether the anchor day of month is enabled
      anchorDayOfMonthEnabled: false,
      // The day of the month to anchor billing on
      anchorDayOfMonth: null,
      // Whether deferred billing is enabled
      deferBillingEnabled: false,
      // The date billing should start
      billingStartsAt: null,
      // null = unlimited (status quo). A positive integer caps weekly attendance.
      classPassQuantity: null,
      limitClassesPerWeek: false,
      money: {
        decimal: '.',
        thousands: ',',
        prefix: '$ ',
        suffix: '',
        precision: 2,
        masked: false,
      },

      expanded: false,
    };
  },
  watch: {
    anchorDayOfMonthEnabled(enabled) {
      if (enabled) {
        this.deferBillingEnabled = false;
        this.billingStartsAt = null;
      }
    },
    deferBillingEnabled(enabled) {
      if (enabled) {
        this.anchorDayOfMonthEnabled = false;
        this.anchorDayOfMonth = null;
      }
    },
    siblingCoupon(siblingCouponId) {
      if (siblingCouponId === -1 || siblingCouponId === null) {
        // Also reset the third coupon
        this.thirdSiblingCoupon = -1;
      } else if (this.thirdSiblingCoupon === -1) {
        this.thirdSiblingCoupon = siblingCouponId;
      }
    },
  },
  computed: {
    // This component is rendered once per pricing tier inside a v-for, so any
    // hardcoded element id collides across tiers. `<label for>` always targets
    // the FIRST matching id, so clicking a lower tier's label focuses tier 1's
    // input. Suffix ids per instance to keep labels wired to their own fields.
    fieldUid() {
      // Prefix by identifier type so a persisted price (id: 1) and a draft
      // (newOptionId: 1) on screen together can't produce the same DOM id.
      // Mirrors the parent v-for :key scheme in paid_session_form.
      if (_.get(this.price, 'id')) {
        return `sp-${this.price.id}`;
      }
      if (_.get(this.price, 'newOptionId')) {
        return `new-${this.price.newOptionId}`;
      }
      return `uid-${this._uid}`;
    },
    legacyCouponBehavior() {
      return this.couponStrategy === 'all';
    },
    selectedCouponId() {
      if (!this.siblingDiscountEnabled || this.siblingCoupon === -1) {
        return null;
      }

      return this.siblingCoupon;
    },
    eligibleForAnchoring() {
      return this.interval === 'month' && this.indefinite;
    },
    selectedCouponCode() {
      if (!this.selectedCouponId) {
        return null;
      }

      for (const coupon of this.availableCoupons) {
        if (coupon.id === this.selectedCouponId) {
          return coupon.code;
        }
      }

      return null;
    },
    selectedThirdCouponId() {
      if (!this.siblingDiscountEnabled || this.thirdSiblingCoupon === -1) {
        return null;
      }

      return this.thirdSiblingCoupon;
    },
    selectedThirdCouponCode() {
      if (!this.selectedThirdCouponId) {
        return null;
      }

      for (const coupon of this.availableCoupons) {
        if (coupon.id === this.selectedThirdCouponId) {
          return coupon.code;
        }
      }

      return null;
    },
    prettyInterval() {
      const plural = this.intervalCount > 1;
      const interval = plural ? `${this.interval}s` : this.interval;
      return `every ${this.intervalCount} ${interval}`;
    },
    indefinite() {
      return this.membershipLength === 'indefinite';
    },
  },
  mounted() {
    const vm = this;
    const { price } = this;

    vm.existingId = _.get(price, 'id', null);
    vm.name = _.get(price, 'name', '');
    vm.amount = _.get(price, 'amount', 0);
    vm.active = _.get(price, 'active', true);
    vm.currency = _.get(price, 'currency', 'usd');
    // v money expects a float, api returns cents
    if (vm.amount > 0) {
      vm.amount /= 100.00;
    }
    vm.couponStrategy = _.get(price, 'coupon_strategy', 'tiers');
    vm.siblingCoupon = _.get(price, 'sibling_coupon_id', -1);
    if (vm.siblingCoupon > 0) {
      vm.siblingDiscountEnabled = true;
    }
    vm.thirdSiblingCoupon = _.get(price, 'third_sibling_coupon_id', -1);
    vm.interval = _.get(price, 'interval', null);
    vm.intervalCount = _.get(price, 'interval_count', 1);
    const intervals = _.get(price, 'intervals_to_bill', null);
    if (intervals) {
      vm.membershipLength = 'limited';
      vm.intervalsToBill = intervals;
    }

    vm.anchorDayOfMonth = _.get(price, 'anchor_day_of_month', null);
    vm.anchorDayOfMonthEnabled = vm.anchorDayOfMonth !== null;

    const billingStartsAt = _.get(price, 'billing_starts_at', null);
    vm.billingStartsAt = billingStartsAt ? moment(billingStartsAt) : null;
    vm.deferBillingEnabled = billingStartsAt !== null;

    vm.oneTimeFeeAmount = _.get(price, 'one_time_fee_amount', 0);
    // v money expects a float, api returns cents
    if (vm.oneTimeFeeAmount > 0) {
      vm.oneTimeFeeAmount /= 100.00;
    }

    vm.classPassQuantity = _.get(price, 'class_pass_quantity', null);
    vm.limitClassesPerWeek = Number.isInteger(vm.classPassQuantity) && vm.classPassQuantity > 0;

    if (!vm.existingId) {
      vm.isNewOption = true;
      vm.toggleExpanded();
    }

    vm.loadCoupons();
  },
  methods: {
    disablePastDates(date) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      return date <= today;
    },
    loadCoupons() {
      const vm = this;

      const url = vm.$apiService.couponsUrl();
      vm.$apiService.loadAllPages(url, 'coupons')
        .then((objects) => {
          vm.availableCoupons = objects;
        })
        .catch((error) => {
          console.error(error);
        });
    },
    toggleExpanded() {
      this.expanded = !this.expanded;
      this.$parent.$emit('editing-subscription-option');
    },
    cancel() {
      this.expanded = !this.expanded;
      this.$parent.$emit('editing-subscription-option-cancelled', this.price.newOptionId, this.isNewOption);
    },
    removeDraft() {
      // todo make remove work for existing payment prices
      if (!this.existingId) {
        this.$parent.$emit('discard-subscription-option-draft', this.price.newOptionId);
      }
    },
    commitPendingNumberInputs() {
      // Force focused number fields to commit before we read/emit. v-model's
      // composition guard can hold a stale value until the field blurs, so
      // tapping a stepper (or typing) then hitting Done without clicking away
      // would otherwise validate/emit the previous value — most visible on
      // touch. Each assignment mirrors its own v-model modifier so types don't
      // drift. The interval fields are disabled once existingId is set (nothing
      // to commit there), so we only touch them for new options.
      const { intervalCountInput, intervalsToBillInput, classPassInput } = this.$refs;
      if (!this.existingId && intervalCountInput) {
        this.intervalCount = intervalCountInput.value;
      }
      if (!this.existingId && intervalsToBillInput) {
        this.intervalsToBill = intervalsToBillInput.value;
      }
      if (this.limitClassesPerWeek && classPassInput) {
        // Mirror the `.number` modifier (Vue's toNumber): keep the parsed
        // number, fall back to the raw string so the validation below still
        // catches blanks/decimals rather than silently coercing them.
        const parsed = parseFloat(classPassInput.value);
        this.classPassQuantity = Number.isNaN(parsed) ? classPassInput.value : parsed;
      }
    },
    onLimitClassesPerWeekChange() {
      // Toggling on defaults to 1 class/week; toggling off clears the limit so
      // the plan serializes back to unlimited (null).
      if (this.limitClassesPerWeek) {
        if (!Number.isInteger(this.classPassQuantity) || this.classPassQuantity < 1) {
          this.classPassQuantity = 1;
        }
      } else {
        this.classPassQuantity = null;
      }
    },
    validate() {
      const vm = this;

      // Commit whatever is currently in the number inputs, even if one still
      // has focus. A stepper tap (or typed value) can be suppressed by
      // v-model's composition guard until the field blurs, so tapping Done
      // without clicking away first would otherwise validate a stale value.
      vm.commitPendingNumberInputs();

      if (!vm.name || !vm.amount || !vm.interval || !vm.intervalCount) {
        vm.showError('Please ensure name, amount, interval and interval count are not empty');
        return;
      }

      if (vm.anchorDayOfMonthEnabled && !vm.anchorDayOfMonth) {
        vm.showError('Please select an anchor day of month or deselect the anchor day of month option');
        return;
      }

      if (vm.deferBillingEnabled && !vm.billingStartsAt) {
        vm.showError('Please select a billing start date or deselect the deferred billing option');
        return;
      }

      // Guard against -1/1.5 etc. silently serializing to null (unlimited)
      const classPassProvided = vm.classPassQuantity !== null
        && vm.classPassQuantity !== ''
        && vm.classPassQuantity !== undefined;
      if (classPassProvided && (!Number.isInteger(vm.classPassQuantity) || vm.classPassQuantity < 1)) {
        vm.showError('Classes per week must be a whole number greater than 0');
        return;
      }

      vm.expanded = !vm.expanded;
      // This is used to distinguish whether it's completely new or whether it's just updated
      vm.isNewOption = false;
      vm.draft = true;
      vm.$parent.$emit('subscription-option-updated', vm.getUpdatedPriceObject());
    },
    getUpdatedPriceObject() {
      const vm = this;
      return {
        draft: true,
        id: vm.existingId,
        active: vm.active,
        newOptionId: vm.price.newOptionId,
        name: vm.name,
        amount: _.round(vm.amount * 100),
        interval: vm.interval,
        currency: vm.currency,
        interval_count: vm.intervalCount,
        intervals_to_bill: vm.indefinite ? null : vm.intervalsToBill,
        sibling_coupon_id: vm.selectedCouponId,
        third_sibling_coupon_id: vm.selectedThirdCouponId,
        one_time_fee_amount: _.round(vm.oneTimeFeeAmount * 100),
        anchor_day_of_month: vm.anchorDayOfMonthEnabled && vm.anchorDayOfMonth && vm.eligibleForAnchoring ? vm.anchorDayOfMonth : null,
        billing_starts_at: vm.deferBillingEnabled && vm.billingStartsAt ? vm.billingStartsAt.format('YYYY-MM-DD') : null,
        class_pass_quantity: Number.isInteger(vm.classPassQuantity) && vm.classPassQuantity > 0 ? vm.classPassQuantity : null,
      };
    },
  },
};
</script>
